import React from 'react';
import { RULE_COLORS, RULE_OPERATORS } from '../../utils/reportTable';

const newId = () => Math.random().toString(36).slice(2, 8);

/** A row of choices, one of which is on. */
const Choice = ({ label, value, options, onChange }) => (
  <div className="rf-format-field">
    <span>{label}</span>
    <div className="rf-segmented" role="radiogroup" aria-label={label}>
      {options.map(([v, text]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? 'is-on' : ''} onClick={() => onChange(v)}>{text}</button>
      ))}
    </div>
  </div>
);

const STACKING = [['auto', 'Automatic'], ['none', 'Side by side'], ['stacked', 'Stacked'], ['percent', 'Stacked to 100%']];

/** The chart's own options: which way bars run and how they stack, bars or lines on a combined chart, and the pie's style. */
const ChartOptions = ({ form, columns, update }) => {
  const t = form.displayType;
  if (!['bar', 'area', 'combo', 'pie'].includes(t)) return null;
  // On a combined chart every value after the first is a line until chosen otherwise.
  const lines = Array.isArray(form.lineSeries) ? form.lineSeries : columns.slice(1).map((c) => c.key);
  const setKind = (key, kind) => {
    const next = kind === 'line' ? [...lines.filter((k) => k !== key), key] : lines.filter((k) => k !== key);
    update({ lineSeries: next });
  };
  return (
    <section className="rf-format-section" aria-label="Chart">
      <h4>Chart</h4>
      {t === 'bar' && (
        <Choice label="Bars" value={form.orientation === 'horizontal' ? 'horizontal' : 'vertical'} onChange={(v) => update({ orientation: v })}
          options={[['vertical', 'Upright'], ['horizontal', 'Sideways']]} />
      )}
      {t === 'bar' && form.orientation === 'horizontal' && <div className="rf-field-note">Sideways bars suit long names, such as customers or products, ranked from the top.</div>}
      {(t === 'bar' || t === 'area') && (
        <>
          <Choice label="Stacking" value={form.stacking || 'auto'} onChange={(v) => update({ stacking: v })} options={STACKING} />
          <div className="rf-field-note">Stacking applies when a second field in Group by splits each {t === 'bar' ? 'bar' : 'area'}, or with several values. Stacked to 100% shows each part&apos;s share.</div>
        </>
      )}
      {t === 'combo' && (
        <>
          {columns.map((c) => (
            <Choice key={c.key} label={c.label} value={lines.includes(c.key) ? 'line' : 'bar'} onChange={(v) => setKind(c.key, v)}
              options={[['bar', 'Bars'], ['line', 'Line']]} />
          ))}
          <div className="rf-field-note">A line of a different kind of value from the bars, such as a percentage against money, gets its own scale on the right.</div>
        </>
      )}
      {t === 'pie' && (
        <>
          <Choice label="Style" value={form.pieStyle === 'pie' ? 'pie' : 'donut'} onChange={(v) => update({ pieStyle: v })}
            options={[['donut', 'Ring, with the total'], ['pie', 'Full pie']]} />
          <label className="rf-format-field">
            <span>Slices shown</span>
            <select aria-label="Slices shown" value={form.pieSlices || 8} onChange={(e) => update({ pieSlices: Number(e.target.value) })}>
              {[4, 5, 6, 8, 10, 12].map((n) => <option key={n} value={n}>{n}, the rest as Other</option>)}
            </select>
          </label>
        </>
      )}
    </section>
  );
};

/** The columns a colour rule can apply to: the values and the calculations. */
function ruleColumns(form, label, formatOf) {
  return [
    ...form.measures.map((m) => ({ key: m, label: label(m), format: formatOf(m) })),
    ...(form.calculations || []).map((c) => ({ key: `calc:${c.id}`, label: c.label, format: c.format || (['percentOfTotal', 'percentChange'].includes(c.type) ? 'percent' : null) })),
  ];
}

/** One colour rule: "Gross profit is below 0 → Red", or a colour scale over a column. */
const ColorRule = ({ rule, columns, percentColumns, onChange, onRemove }) => {
  const column = columns.find((c) => c.key === rule.column);
  const percent = percentColumns.has(rule.column);
  const name = column?.label || 'a column';
  if (rule.kind === 'scale') {
    return (
      <div className="rf-rule" role="group" aria-label={`Colour scale on ${name}`}>
        <div className="rf-rule-line">
          <select aria-label="Column" value={rule.column} onChange={(e) => onChange({ ...rule, column: e.target.value })}>
            {columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <span>shaded from red to green</span>
        </div>
        <div className="rf-rule-line">
          <div className="rf-segmented" role="radiogroup" aria-label={`Which end of ${name} is good`}>
            {[[true, 'Higher is better'], [false, 'Lower is better']].map(([v, l]) => (
              <button key={l} type="button" role="radio" aria-checked={rule.higherIsBetter === v} className={rule.higherIsBetter === v ? 'is-on' : ''}
                onClick={() => onChange({ ...rule, higherIsBetter: v })}>{l}</button>
            ))}
          </div>
          <button type="button" className="rf-btn rf-btn--ghost rf-btn--small rf-rule-remove" onClick={onRemove} aria-label={`Remove the colour scale on ${name}`}>Remove</button>
        </div>
      </div>
    );
  }
  return (
    <div className="rf-rule" role="group" aria-label={`Colour rule on ${name}`}>
      <div className="rf-rule-line">
        <span className="rf-rule-when">When</span>
        <select aria-label="Column" value={rule.column} onChange={(e) => onChange({ ...rule, column: e.target.value })}>
          {columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div className="rf-rule-line">
        <select aria-label="Comparison" value={rule.operator} onChange={(e) => onChange({ ...rule, operator: e.target.value })}>
          {RULE_OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="rf-rule-number">
          <input type="number" step="any" aria-label="Value" value={rule.value} onChange={(e) => onChange({ ...rule, value: e.target.value })} />
          {percent && <span aria-hidden="true">%</span>}
        </span>
        {rule.operator === 'between' && (
          <>
            <span>and</span>
            <span className="rf-rule-number">
              <input type="number" step="any" aria-label="Second value" value={rule.value2 ?? ''} onChange={(e) => onChange({ ...rule, value2: e.target.value })} />
              {percent && <span aria-hidden="true">%</span>}
            </span>
          </>
        )}
      </div>
      <div className="rf-rule-line">
        <span className="rf-rule-when">show it</span>
        <div className="rf-swatches" role="radiogroup" aria-label="Colour">
          {Object.entries(RULE_COLORS).map(([key, c]) => (
            <button key={key} type="button" role="radio" aria-checked={rule.color === key} aria-label={c.label} title={c.label}
              className={`rf-swatch ${rule.color === key ? 'is-on' : ''}`} style={{ '--swatch': c.hex }} onClick={() => onChange({ ...rule, color: key })} />
          ))}
        </div>
        <button type="button" className="rf-btn rf-btn--ghost rf-btn--small rf-rule-remove" onClick={onRemove} aria-label={`Remove the colour rule on ${name}`}>Remove</button>
      </div>
    </div>
  );
};

/** Right pane, Format tab: pivot layout, totals, colour rules, target, description, refresh and visibility. */
const FormatPanel = ({ form, catalogue, isAdmin, update }) => {
  const label = (name) => catalogue.fields.get(name)?.label || name.split('.').pop();
  const formatOf = (name) => catalogue.fields.get(name)?.format || null;
  const groups = [...form.dimensions, ...(form.timeDimension && form.granularity ? [form.timeDimension] : [])];
  const placeOf = (d) => (form.pivotColumns.includes(d) ? 'columns' : form.pivotRows.includes(d) ? 'rows' : null);
  const place = (d, where) => update({
    pivotRows: where === 'rows' ? [...form.pivotRows.filter((x) => x !== d), d] : form.pivotRows.filter((x) => x !== d),
    pivotColumns: where === 'columns' ? [...form.pivotColumns.filter((x) => x !== d), d] : form.pivotColumns.filter((x) => x !== d),
  });
  const chartish = ['bar', 'line', 'area', 'combo', 'stat'].includes(form.displayType);
  const columns = ruleColumns(form, label, formatOf);
  const percentColumns = new Set(columns.filter((c) => c.format === 'percent').map((c) => c.key));
  const rules = form.colorRules || [];
  const setRules = (list) => update({ colorRules: list });
  const colorable = form.displayType !== 'pie';

  return (
    <div className="rf-format">
      <ChartOptions form={form} columns={columns} update={update} />
      {form.displayType === 'pivot' && (
        <section className="rf-format-section" aria-label="Pivot layout">
          <h4>Pivot layout</h4>
          {groups.length === 0 && <div className="rf-field-note">Add fields to Group by or a time breakdown to lay out the pivot.</div>}
          {groups.map((d) => (
            <div key={d} className="rf-pivot-row">
              <span>{label(d)}</span>
              <div className="rf-segmented" role="radiogroup" aria-label={`Place ${label(d)}`}>
                <button type="button" role="radio" aria-checked={placeOf(d) === 'rows'} className={placeOf(d) === 'rows' ? 'is-on' : ''} onClick={() => place(d, 'rows')}>Rows</button>
                <button type="button" role="radio" aria-checked={placeOf(d) === 'columns'} className={placeOf(d) === 'columns' ? 'is-on' : ''} onClick={() => place(d, 'columns')}>Columns</button>
              </div>
            </div>
          ))}
          {columns.length > 0 && (
            <label className="rf-format-field">
              <span>Cell value</span>
              <select aria-label="Cell value" value={form.pivotMeasure || form.measures[0]} onChange={(e) => update({ pivotMeasure: e.target.value })}>
                {columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
          )}
          <label className="rf-switch">
            <input type="checkbox" checked={form.pivotTotalColumn !== false} onChange={(e) => update({ pivotTotalColumn: e.target.checked })} />
            <span>Show a total column</span>
          </label>
        </section>
      )}

      {form.displayType === 'table' && (
        <section className="rf-format-section" aria-label="Totals">
          <h4>Totals</h4>
          <label className="rf-switch">
            <input type="checkbox" checked={Boolean(form.totalRow)} onChange={(e) => update({ totalRow: e.target.checked })} />
            <span>Show a total row</span>
          </label>
          <div className="rf-field-note">Totals are worked out from all the data in the period, so averages and rates are right and rows beyond the limit still count.</div>
        </section>
      )}

      {colorable && (
        <section className="rf-format-section" aria-label="Colour rules">
          <h4>Colour rules</h4>
          {columns.length === 0 && <div className="rf-field-note">Add a value to colour it by rules.</div>}
          {rules.map((r) => (
            <ColorRule key={r.id} rule={r} columns={columns} percentColumns={percentColumns}
              onChange={(next) => setRules(rules.map((x) => (x.id === r.id ? next : x)))}
              onRemove={() => setRules(rules.filter((x) => x.id !== r.id))} />
          ))}
          {columns.length > 0 && (
            <div className="rf-rule-add">
              <button type="button" className="rf-btn rf-btn--ghost rf-btn--small"
                onClick={() => setRules([...rules, { id: newId(), column: columns[0].key, kind: 'rule', operator: 'lt', value: '0', color: 'red' }])}>
                + Add a rule
              </button>
              {form.displayType !== 'stat' && (
                <button type="button" className="rf-btn rf-btn--ghost rf-btn--small"
                  onClick={() => setRules([...rules, { id: newId(), column: columns[0].key, kind: 'scale', higherIsBetter: true }])}>
                  + Add a colour scale
                </button>
              )}
            </div>
          )}
          {rules.length > 0 && (
            <div className="rf-field-note">
              {form.displayType === 'stat' ? 'The number takes the colour of the first rule it matches.'
                : chartish ? 'Bars take the colour of the first rule they match.'
                  : 'Cells take the colour of the first rule they match; a colour scale shades the rest. The total row is left plain.'}
            </div>
          )}
        </section>
      )}

      <section className="rf-format-section" aria-label="Target">
        <h4>Target</h4>
        <label className="rf-format-field">
          <span>Target line</span>
          <input type="number" step="any" aria-label="Target line" value={form.target} placeholder="e.g. 15000"
            onChange={(e) => update({ target: e.target.value })} />
        </label>
        <div className="rf-field-note">
          {chartish ? 'Shown as a dashed line on charts and against the stat value.' : 'Shown on bar, line, area and stat displays.'}
        </div>
      </section>

      <section className="rf-format-section" aria-label="Details">
        <h4>Details</h4>
        <label className="rf-format-field">
          <span>Description</span>
          <textarea aria-label="Description" rows={3} value={form.description || ''} placeholder="What this report shows and who uses it"
            onChange={(e) => update({ description: e.target.value })} />
        </label>
        <label className="rf-format-field">
          <span>Refresh every (minutes)</span>
          <input type="number" min="1" max="1440" aria-label="Refresh every (minutes)" value={form.refreshMinutes}
            onChange={(e) => update({ refreshMinutes: e.target.value })} />
        </label>
        {isAdmin && (
          <label className="rf-format-field">
            <span>Visibility</span>
            <select aria-label="Visibility" value={form.visibility || 'company'} onChange={(e) => update({ visibility: e.target.value })}>
              <option value="company">Everyone in the company</option>
              <option value="private">Only me</option>
            </select>
          </label>
        )}
      </section>
    </div>
  );
};

export default FormatPanel;
